import { useEffect, useState } from "preact/hooks";
import { Customers, Settings as SettingsApi } from "../lib/api";
import type { Customer, Supplier } from "@shared/types";
import { describeError } from "../lib/errors";

export function Settings() {
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [editing, setEditing] = useState<Customer | null>(null);

  useEffect(() => {
    SettingsApi.getSupplier()
      .then((r) => setSupplier(r.supplier))
      .catch((e) => setError(describeError(e)));
    Customers.list()
      .then((r) => setCustomers(r.customers))
      .catch(() => {});
  }, []);

  function patchSupplier<K extends keyof Supplier>(key: K, v: Supplier[K]) {
    setSupplier((prev) => (prev ? { ...prev, [key]: v } : prev));
  }

  async function saveSupplier(e: Event) {
    e.preventDefault();
    if (!supplier) return;
    setSavingSupplier(true);
    setError(null);
    try {
      const { id, created_at, updated_at, ...patch } = supplier;
      void id;
      void created_at;
      void updated_at;
      const r = await SettingsApi.updateSupplier(patch);
      setSupplier(r.supplier);
      setSavedAt(new Date().toLocaleTimeString("sk-SK"));
    } catch (e) {
      setError(describeError(e));
    } finally {
      setSavingSupplier(false);
    }
  }

  async function onSignatureFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !supplier) return;
    const buf = await file.arrayBuffer();
    let bin = "";
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
    const b64 = btoa(bin);
    patchSupplier("signature_image_b64", b64);
  }

  if (error && !supplier)
    return (
      <main class="app-shell">
        <p class="error">{error}</p>
      </main>
    );
  if (!supplier)
    return (
      <main class="app-shell">
        <p class="muted">Načítavam…</p>
      </main>
    );

  return (
    <main class="app-shell">
      <h1>Nastavenia</h1>

      <form class="card" onSubmit={saveSupplier}>
        <h2>Dodávateľ</h2>
        <label class="field">
          <span>Názov</span>
          <input
            value={supplier.name}
            onInput={(e) =>
              patchSupplier("name", (e.target as HTMLInputElement).value)
            }
          />
        </label>
        <div class="grid-2">
          <SupplierField
            label="Ulica a č."
            value={supplier.address_line1}
            onChange={(v) => patchSupplier("address_line1", v)}
          />
          <SupplierField
            label="Mesto"
            value={supplier.address_city}
            onChange={(v) => patchSupplier("address_city", v)}
          />
          <SupplierField
            label="PSČ"
            value={supplier.address_zip}
            onChange={(v) => patchSupplier("address_zip", v)}
          />
          <SupplierField
            label="Štát"
            value={supplier.address_country}
            onChange={(v) => patchSupplier("address_country", v)}
          />
          <SupplierField
            label="IČO"
            value={supplier.ico ?? ""}
            onChange={(v) => patchSupplier("ico", v || null)}
          />
          <SupplierField
            label="DIČ"
            value={supplier.dic ?? ""}
            onChange={(v) => patchSupplier("dic", v || null)}
          />
          <SupplierField
            label="IČ DPH"
            value={supplier.ic_dph ?? ""}
            onChange={(v) => patchSupplier("ic_dph", v || null)}
          />
          <SupplierField
            label="IBAN"
            value={supplier.iban ?? ""}
            onChange={(v) => patchSupplier("iban", v || null)}
          />
          <SupplierField
            label="SWIFT/BIC"
            value={supplier.swift ?? ""}
            onChange={(v) => patchSupplier("swift", v || null)}
          />
          <SupplierField
            label="Banka"
            value={supplier.bank_name ?? ""}
            onChange={(v) => patchSupplier("bank_name", v || null)}
          />
          <SupplierField
            label="E-mail"
            value={supplier.email ?? ""}
            onChange={(v) => patchSupplier("email", v || null)}
          />
          <SupplierField
            label="Web"
            value={supplier.website ?? ""}
            onChange={(v) => patchSupplier("website", v || null)}
          />
          <SupplierField
            label="Telefón"
            value={supplier.phone ?? ""}
            onChange={(v) => patchSupplier("phone", v || null)}
          />
        </div>

        <div class="signature">
          <span class="field__label">Podpis (PNG)</span>
          {supplier.signature_image_b64 ? (
            <img
              class="signature__preview"
              src={`data:image/png;base64,${supplier.signature_image_b64}`}
              alt="Podpis"
            />
          ) : (
            <p class="muted">— žiadny podpis —</p>
          )}
          <input type="file" accept="image/png" onChange={onSignatureFile} />
          {supplier.signature_image_b64 && (
            <button
              type="button"
              class="btn btn--sm btn--ghost"
              onClick={() => patchSupplier("signature_image_b64", null)}
            >
              Odstrániť podpis
            </button>
          )}
        </div>

        {error && <p class="error">{error}</p>}
        <div class="card__actions">
          <button class="btn primary" type="submit" disabled={savingSupplier}>
            {savingSupplier ? "Ukladám…" : "Uložiť"}
          </button>
          {savedAt && <span class="muted">Uložené {savedAt}</span>}
        </div>
      </form>

      <section class="card">
        <h2>Odberatelia</h2>
        {customers.length === 0 ? (
          <p class="muted">Žiadni odberatelia.</p>
        ) : (
          <table class="table">
            <thead>
              <tr>
                <th>Názov</th>
                <th>IČO</th>
                <th>Mesto</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.ico ?? "—"}</td>
                  <td>{c.address_city ?? "—"}</td>
                  <td class="row-actions">
                    <button
                      type="button"
                      class="btn btn--sm"
                      onClick={() => setEditing(c)}
                    >
                      Upraviť
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {editing && (
        <CustomerEditDialog
          customer={editing}
          onClose={() => setEditing(null)}
          onSaved={(c) => {
            setCustomers((prev) => prev.map((p) => (p.id === c.id ? c : p)));
            setEditing(null);
          }}
        />
      )}
    </main>
  );
}

function SupplierField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label class="field">
      <span>{label}</span>
      <input
        value={value}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
      />
    </label>
  );
}

function CustomerEditDialog({
  customer,
  onClose,
  onSaved,
}: {
  customer: Customer;
  onClose: () => void;
  onSaved: (c: Customer) => void;
}) {
  const [form, setForm] = useState<Customer>(customer);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function set<K extends keyof Customer>(k: K, v: Customer[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: Event) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const r = await Customers.update(customer.id, {
        name: form.name,
        address_line1: form.address_line1 ?? "",
        address_city: form.address_city ?? "",
        address_zip: form.address_zip ?? "",
        address_country: form.address_country,
        ico: form.ico ?? "",
        dic: form.dic ?? "",
        ic_dph: form.ic_dph ?? "",
      });
      onSaved(r.customer);
    } catch (e) {
      setErr(describeError(e));
      setSaving(false);
    }
  }

  return (
    <div class="modal" role="dialog" aria-modal="true">
      <div class="modal__backdrop" onClick={onClose} />
      <form class="modal__card" onSubmit={submit}>
        <h2>Upraviť odberateľa</h2>
        <label class="field">
          <span>Názov</span>
          <input
            value={form.name}
            onInput={(e) =>
              set("name", (e.target as HTMLInputElement).value)
            }
            required
          />
        </label>
        <div class="grid-2">
          <Input label="Ulica a č." value={form.address_line1 ?? ""} on={(v) => set("address_line1", v)} />
          <Input label="Mesto" value={form.address_city ?? ""} on={(v) => set("address_city", v)} />
          <Input label="PSČ" value={form.address_zip ?? ""} on={(v) => set("address_zip", v)} />
          <Input label="Štát" value={form.address_country} on={(v) => set("address_country", v)} />
          <Input label="IČO" value={form.ico ?? ""} on={(v) => set("ico", v)} />
          <Input label="DIČ" value={form.dic ?? ""} on={(v) => set("dic", v)} />
          <Input label="IČ DPH" value={form.ic_dph ?? ""} on={(v) => set("ic_dph", v)} />
        </div>
        {err && <p class="error">{err}</p>}
        <div class="modal__actions">
          <button type="button" class="btn" onClick={onClose} disabled={saving}>
            Zrušiť
          </button>
          <button class="btn primary" type="submit" disabled={saving}>
            {saving ? "Ukladám…" : "Uložiť"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({
  label,
  value,
  on,
}: {
  label: string;
  value: string;
  on: (v: string) => void;
}) {
  return (
    <label class="field">
      <span>{label}</span>
      <input
        value={value}
        onInput={(e) => on((e.target as HTMLInputElement).value)}
      />
    </label>
  );
}
